import { Component, inject, input, output, signal } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { faFileExcel, faFilePdf, faPrint, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { faComments, faFolder } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { MatCheckboxChange, MatCheckboxModule } from '@angular/material/checkbox';
import { ApplicationApiService, AttachmentDto, AttachmentType, CurrentConditionDto, FileResponse, RoadmapDto, RoadmapTargetDto, TargetCategoryDto, TargetConditionDto, UserProfile } from 'src/app/services/application-api.service';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ConstantInfoVariables } from 'src/app/types/constants';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { FormsModule } from '@angular/forms';
import { MatBadgeModule } from '@angular/material/badge';
import { DatePipe } from '@angular/common';
import { RoadmapAttachmentsComponent, RoadmapAttachmentsDialogData } from './roadmap-attachments/roadmap-attachments.component';
import { RoadmapCommentsComponent, RoadmapCommentsDialogData } from './roadmap-comments/roadmap-comments.component';
import { MatDialog } from '@angular/material/dialog';
import { RoadmapGoalPlanComponent, RoadmapGoalPlanDialogData } from './roadmap-goal-plan/roadmap-goal-plan.component';
import { saveAs } from 'file-saver';
import { LoadingComponent } from "src/app/components/loading/loading.component";
import { UserProfileService } from 'services';

@Component({
  selector: 'roadmap-goals',
  imports: [MatButton, MatIconModule, FontAwesomeModule, MatCheckboxModule, MatTooltipModule, MatFormFieldModule, MatInputModule, MatDatepickerModule, MatSelectModule, FormsModule,
    MatBadgeModule, LoadingComponent],
  templateUrl: './roadmap-goals.component.html',
  styleUrl: './roadmap-goals.component.scss'
})
export class RoadmapGoalsComponent {
  roadmap = input<RoadmapDto | undefined>(undefined, { alias: 'roadmap' });
  isEditable = input<boolean>(false, { alias: 'isEditable' });
  volumeOver10M = input<boolean>(false, { alias: 'volumeOver10M' });
  currentConditions = input<CurrentConditionDto[]>([], { alias: 'currentConditions' });
  appUser = input<UserProfile | undefined>(undefined, { alias: 'appUser' });
  pdfAction = output<'download' | 'print'>();
  dialog = inject(MatDialog);
  pipe = new DatePipe('en-US');
  isNew = input<boolean>(false, { alias: 'isNew' });
  isLoading = signal(false);
  apiService = inject(ApplicationApiService);
  userService = inject(UserProfileService);

  readonly infoGoalRecurring = ConstantInfoVariables.RoadmapGoalIsRecurring;
  readonly infoGoalRecurringShort = ConstantInfoVariables.RoadmapGoalIsRecurringShort;
  readonly infoGoalNotRecurring = ConstantInfoVariables.RoadmapGoalNotRecurring;
  readonly infoHasComment = ConstantInfoVariables.RoadmapGoalHasComments;
  readonly infoNoComment = ConstantInfoVariables.RoadmapGoalNoComments;

  readonly faFileExcel = faFileExcel;
  readonly faFilePdf = faFilePdf;
  readonly faInfoCircle = faInfoCircle;
  readonly faPrint = faPrint;
  readonly faComments = faComments;
  readonly faFolder = faFolder;

  goalIsRequired(condition: TargetConditionDto): boolean {
    if (this.volumeOver10M()) {
      return condition.isRequired ?? false;
    }
    return false;
  }

  leaderIsRequired(condition: TargetConditionDto): boolean {
    if (this.goalIsRequired(condition)) {
      const cid = condition.roadmapTargets?.[0]?.currentConditionId;
      if (cid === 1 || cid === 2 || cid === 4)
        return true;
      else
        return false;
    }
    return false;
  }

  conditionVersionHasComments(condition: TargetConditionDto): boolean {
    // are there any comments for this condition in the current roadmap version?
    const target = condition.roadmapTargets?.[0];
    const versionComments = target?.roadmapComments?.filter(comment => comment.roadmapVersion === this.roadmap()!.version);
    return versionComments != null && versionComments.length > 0; return false;
  }

  getLastestFullComment(condition: TargetConditionDto): string {
    const target = condition.roadmapTargets?.[0];
    let displayComment = this.infoNoComment;
    if (target?.roadmapComments?.filter(comment => comment.roadmapVersion === this.roadmap()!.version).length! > 0) {
      const latestComment = target?.roadmapComments
        ?.filter(comment => comment.roadmapVersion === this.roadmap()!.version)
        .sort((a, b) => new Date(b.createdOn!).getTime() - new Date(a.createdOn!).getTime())[0];

      const fullComment = `"${latestComment!.commentText}" by ${latestComment!.createdBy} (${latestComment!.type!.toUpperCase()}) on ${this.pipe.transform(latestComment!.createdOn, 'mediumDate')}`;

      // keep comment under 200 characters for tooltip
      displayComment = (fullComment.length >= 200) ? fullComment.slice(0, 196) + '..."' : fullComment;
    }

    return displayComment;
  }

  getLatestCommentType(condition: TargetConditionDto): string | null {
    const target = condition.roadmapTargets?.[0];
    if (target?.roadmapComments?.filter(comment => comment.roadmapVersion === this.roadmap()!.version).length! > 0) {
      const latestComment = target?.roadmapComments
        ?.filter(comment => comment.roadmapVersion === this.roadmap()!.version)
        .sort((a, b) => new Date(b.createdOn!).getTime() - new Date(a.createdOn!).getTime())[0];

      return latestComment!.type!;
    }
    return null;
  }

  getAttachmentsCount(condition: TargetConditionDto): number {
    const target = condition.roadmapTargets?.[0];
    return this.countFileAttachments(target?.attachments ?? []);
  }

  private countFileAttachments(attachments: AttachmentDto[]): number {
    let count = 0;
    for (const attachment of attachments) {
      if (attachment.type === AttachmentType.File) {
        count++;
      }
      if (attachment.children && attachment.children.length > 0) {
        count += this.countFileAttachments(attachment.children);
      }
    }
    return count;
  }

  onRoadmapAttachmentsClick(condition: TargetConditionDto) {
    const target = condition.roadmapTargets?.[0];
    this.dialog.open(RoadmapAttachmentsComponent, {
      data: {
        roadmapId: this.roadmap()!.id!,
        targetConditionId: condition.id!,
        targetConditionName: condition.name!,
        attachments: target?.attachments ?? []
      } as RoadmapAttachmentsDialogData,
      width: '800px'
    });
  }

  plannedGoalCheck(event: MatCheckboxChange, roadmapTarget: RoadmapTargetDto) {
    if (this.isNew()) {
      event.checked ? roadmapTarget.currentConditionId = 4 : roadmapTarget.currentConditionId = 0;
    } else {
      if (!event.checked) {
        roadmapTarget.currentConditionId = 0;
      } else {
        roadmapTarget.currentConditionId = 4;
      }
    }
  }

  onStatusChanged(event: MatSelectChange, roadmapTarget: RoadmapTargetDto) {
    if (event.value === 0) {
      roadmapTarget.isPlannedGoal = false;
    }
  }

  onOpenRoadmapGoalPlanClick(category: TargetCategoryDto, condition: TargetConditionDto) {
    const dialogRef = this.dialog.open(RoadmapGoalPlanComponent, {
      data: {
        roadmapId: this.roadmap()!.id!,
        targetConditionName: condition.name!,
        targetCategoryName: category.name!,
        isLocked: this.isEditable()!,
        targetConditionDescription: condition.description!,
        goalPlan: condition.roadmapTargets?.[0]?.goalPlan ?? ''
      } as RoadmapGoalPlanDialogData,
      width: '800px'
    });

    dialogRef.afterClosed().subscribe((result: string | undefined) => {
      if (result !== undefined && condition.roadmapTargets?.[0]) {
        condition.roadmapTargets[0].goalPlan = result;
      }
    });
  }

  onRoadmapCommentClick(condition: TargetConditionDto) {
    const target = condition.roadmapTargets?.[0];
    this.dialog.open(RoadmapCommentsComponent, {
      data: {
        roadmapId: this.roadmap()!.id!,
        targetConditionId: condition.id!,
        targetConditionName: condition.name!,
        roadmapTargetId: target?.id!,
        appUser: this.appUser(),
        comments: target?.roadmapComments ?? [],
        roadmapVersion: this.roadmap()!.version!,
        userIsApproverForProfitCenter: this.userService.isUserApproverForProfitCenter(this.roadmap()!.profitCenter!.profitCenterCode!),
        userIsApproverForRegion: this.userService.isUserApproverForRegion(this.roadmap()!.profitCenter!.region!.id)
      } as RoadmapCommentsDialogData,
      width: '800px'
    });
  }

  async onExportToExcelClick() {
    this.isLoading.set(true);
    const filename = 'Roadmap' + Date.now() + '.xlsx';
    await this.apiService.apiExportExportExcel(this.roadmap()!.id!, this.roadmap()!.version!)
      .subscribe({
        next: (res: FileResponse) => {
          if (res) {
            saveAs(res.data, filename);
            this.isLoading.set(false);
          } else {
            this.isLoading.set(false);
          }
        },
        error: (err) => {
          this.isLoading.set(false);
        }
      });
  }



  onShowLegendPromptClick() {

  }
}
